import axios from 'axios';

// API base URL from environment or default - use relative URL in development
const API_URL = '';

// Create axios instance
const api = axios.create({
  baseURL: API_URL
});

// Payment processors config
const paymentProcessors = {
  stripe: {
    name: 'Stripe',
    logo: 'https://stripe.com/img/v3/home/twitter.png'
  },
  paypal: {
    name: 'PayPal',
    logo: 'https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg'
  },
  square: {
    name: 'Square',
    logo: 'https://cdn.cms-twdigitalassets.com/content/dam/help-twitter/twitter-logo.png.twimg.1920.png'
  },
  eway: {
    name: 'eWAY',
    logo: 'https://www.eway.com.au/graphics/logo-on-light-lg.png'
  }
};

// Get clinic info by ID or stub
export const getClinicInfo = async (clinicId, clinicStub) => {
  try {
    let params = {};
    if (clinicId) {
      params.id = clinicId;
    } else if (clinicStub) {
      params.stub = clinicStub;
    } else {
      throw new Error('Either clinicId or clinicStub must be provided');
    }
    
    const response = await api.get('/api/clinics', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching clinic info:', error);
    throw error;
  }
};

// Get services (appointment types) for a clinic, optionally filtered by category
export const getClinicServices = async (clinicId, category = null) => {
  try {
    // Construct URL with category parameter if provided
    let url = `/api/clinics/${clinicId}/appointment-types`;
    if (category) {
      url += `?category=${encodeURIComponent(category)}`;
    }
    
    console.log(`Fetching services with URL: ${url}`);
    
    const response = await api.get(url);
    const appointmentTypes = response.data.appointment_types || [];
    
    console.log(`Retrieved ${appointmentTypes.length} appointment types from API${category ? ` in category '${category}'` : ''}`);
    
    // Log details about the appointment types
    appointmentTypes.forEach((service, index) => {
      console.log(`Service ${index + 1}: ${service.name}, duration: ${service.duration_in_minutes}min`);
    });
    
    return appointmentTypes;
  } catch (error) {
    console.error('Error fetching clinic services:', error);
    throw error;
  }
};

// Get practitioners for a clinic, optionally filtered by appointment type
export const getPractitioners = async (clinicId, appointmentTypeId = null) => {
  try {
    const params = { clinic_id: clinicId };
    
    // Add appointment type filter if provided
    if (appointmentTypeId) {
      params.appointment_type_id = appointmentTypeId;
      console.log(`Filtering practitioners for appointment type ID: ${appointmentTypeId}`);
    }
    
    const response = await api.get(`/api/practitioners`, { params });
    
    // Log results for debugging
    if (response.data && response.data.practitioners) {
      console.log(`Retrieved ${response.data.practitioners.length} practitioners ${appointmentTypeId ? 'for service' : ''}`);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching practitioners:', error);
    throw error;
  }
};

// Get available times for appointment
export const getAvailableTimes = async (params, timezone = null) => {
  try {
    // Add timezone to params if provided
    const finalParams = { ...params };
    if (timezone) {
      finalParams.timezone = timezone;
      console.log('Adding timezone to available times request:', timezone);
    }
    
    console.log('Fetching available times with params:', finalParams);
    const response = await api.get('/api/available-times', { params: finalParams });
    
    // Enhanced logging of response
    console.log('Available times response status:', response.status);
    
    if (response.data && response.data.available_times) {
      const count = response.data.available_times.length;
      console.log(`Available times response contains ${count} time slots`);
      
      // Log a sample of times if available
      if (count > 0) {
        console.log('Sample time:', response.data.available_times[0]);
      }
    } else {
      console.log('Response data structure:', Object.keys(response.data || {}));
      console.log('No available_times array in response or empty array');
    }
    
    // Ensure we always return an object with available_times array
    if (!response.data) {
      console.warn('No data in response, returning empty array');
      return { available_times: [] };
    }
    
    if (!response.data.available_times) {
      console.warn('No available_times property in response, creating empty array');
      return { ...response.data, available_times: [] };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching available times:', error);
    // Return empty array rather than throwing
    return { available_times: [], error: error.message };
  }
};

// Create a booking
export const createBooking = async (bookingData) => {
  try {
    const response = await api.post('/api/appointments', bookingData);
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

// Format date for display
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

// Format time for display
export const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format price for display
export const formatPrice = (price) => {
  // For consistency, treat NULL prices as free
  if (price === null || price === undefined) return 'Free';
  if (price === 0) return 'Free';
  if (price === '0') return 'Free';
  
  // Convert string numbers to actual numbers
  if (typeof price === 'string') {
    // Try to parse the string to a number
    const numericPrice = parseFloat(price);
    if (!isNaN(numericPrice)) {
      // If parsing was successful, return formatted number
      return `$${numericPrice.toFixed(2)}`;
    }
    // If parsing failed, just return the string with a dollar sign
    return `$${price}`;
  }
  
  // For numeric values
  return `$${price.toFixed(2)}`;
};

// Initiate payment
export const initiatePayment = async (paymentData) => {
  try {
    const response = await api.post('/api/payments/initiate', paymentData);
    return response.data;
  } catch (error) {
    console.error('Error initiating payment:', error);
    throw error;
  }
};

// Initiate eWay payment directly using the new eWay-specific endpoint
export const initiateEwayPayment = async (paymentData) => {
  try {
    console.log('Initiating Eway payment with data:', paymentData);
    const response = await api.post('/api/payments/eway/initiate', paymentData);
    
    // Log the response for debugging
    console.log('Eway initiate response:', response.data);
    
    // Check if we have the required fields
    if (!response.data.accessCode) {
      console.error('Missing accessCode in response');
    }
    
    if (!response.data.formActionUrl) {
      console.error('Missing formActionUrl in response');
    }
    
    // Ensure we're using the correct URL and parameters
    if (response.data.formUrl && !response.data.formActionUrl) {
      console.warn('Found formUrl but no formActionUrl - using formUrl as fallback');
      response.data.formActionUrl = response.data.formUrl;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error initiating eWay payment:', error);
    throw error;
  }
};

// Process Square payment with token
export const processSquarePayment = async (paymentData) => {
  try {
    console.log('Processing Square payment with token:', paymentData);
    const response = await api.post('/api/payments/square/process', paymentData);
    
    console.log('Square payment response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error processing Square payment:', error);
    
    // Extract error message from response if available
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else {
      throw new Error(error.message || 'Payment processing failed');
    }
  }
};

// Check payment status
export const checkPaymentStatus = async (paymentId, processor, clinicId) => {
  try {
    console.log(`Checking payment status for ID: ${paymentId}, processor: ${processor}, clinic: ${clinicId}`);
    const response = await api.get(`/api/payments/status/${paymentId}`, {
      params: {
        processor,
        clinic_id: clinicId
      }
    });
    console.log('Payment status response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
};

// Check eWay payment status specifically
export const checkEwayPaymentStatus = async (accessCode, clinicId) => {
  try {
    console.log(`Checking eWay payment status for AccessCode: ${accessCode}, clinic: ${clinicId}`);
    
    // Get booking session data to send with the status check for automatic appointment creation
    let sessionData = null;
    try {
      sessionData = sessionStorage.getItem('bookingSession');
      if (sessionData) {
        console.log('Including booking session data with status check');
      }
    } catch (err) {
      console.warn('Unable to access sessionStorage:', err);
    }
    
    // Add the session data to request headers
    const headers = {};
    if (sessionData) {
      headers['x-booking-session'] = sessionData;
    }
    
    const response = await api.get(`/api/payments/eway/status/${accessCode}`, {
      params: {
        clinic_id: clinicId,
        session_data: sessionData // Also include as a query param as fallback
      },
      headers
    });
    
    console.log('eWay payment status response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error checking eWay payment status:', error);
    throw error;
  }
};

// Complete booking after successful payment
export const completeBooking = async (bookingData) => {
  try {
    console.log('Completing booking with data:', bookingData);
    
    // Transform the booking data to match the API endpoint format
    const appointmentData = {
      clinic_id: bookingData.clinic_id,
      appointment_type_id: bookingData.appointment_type_id,
      practitioner_id: bookingData.practitioner_id,
      starts_at: bookingData.starts_at,
      ends_at: bookingData.ends_at,
      patient_first_name: (bookingData.patient_name || '').split(' ')[0] || 'Unknown',
      patient_last_name: (bookingData.patient_name || '').split(' ').slice(1).join(' ') || 'Patient',
      patient_email: bookingData.patient_email,
      patient_phone: bookingData.patient_phone || '',
      notes: bookingData.notes || ''
    };
    
    console.log('Sending appointment data:', appointmentData);
    
    const response = await api.post('/api/appointments', appointmentData);
    
    console.log('Booking completed successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error completing booking:', error);
    
    // Extract error message from response if available
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else {
      throw new Error(error.message || 'Booking creation failed');
    }
  }
};

// Get payment processor info
export const getPaymentProcessorInfo = (processorType) => {
  return paymentProcessors[processorType.toLowerCase()] || {
    name: processorType,
    logo: null
  };
};

/**
 * Send blood test email to user
 * @param {Object} data - Email data
 * @param {string} data.email - Recipient email
 * @param {string} data.bloodTestUrl - iMedical blood test URL
 * @param {string} data.bloodTestType - Type of blood test
 * @param {string} data.bloodTestPrice - Price of blood test
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendBloodTestEmail = async (data) => {
  try {
    console.log('Sending blood test email:', { email: data.email, type: data.bloodTestType });

    const response = await api.post('/api/blood-test/send-email', {
      email: data.email,
      bloodTestUrl: data.bloodTestUrl,
      bloodTestType: data.bloodTestType,
      bloodTestPrice: data.bloodTestPrice
    });

    console.log('Blood test email response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to send blood test email:', error);

    // Extract error message from response if available
    if (error.response?.data?.error) {
      return { success: false, error: error.response.data.error };
    }

    return { success: false, error: error.message || 'Failed to send email' };
  }
};